import { Button } from "@/components/ui/button";
import { LifeShockEvent, LifeShockChoice, fmt } from "@/hooks/useHomeBuyingSim";
import { AlertTriangle, Wallet, CreditCard, Clock } from "lucide-react";

interface Props {
  event: LifeShockEvent;
  liquidAfter: number;
  emergencyFund: number;
  onChoose: (c: LifeShockChoice) => void;
  onContinue: () => void;
}

export default function LifeShockScreen({ event, liquidAfter, emergencyFund, onChoose, onContinue }: Props) {
  const chosen = event.choice;
  const cashAvailable = liquidAfter + emergencyFund;
  const canPayCash = cashAvailable >= event.amount;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex w-12 h-12 rounded-2xl items-center justify-center bg-amber-500/10 mx-auto">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
        </div>
        <h2 className="font-heading text-lg font-bold text-foreground">Surprise: Homeownership Has a Bill</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">{event.description}</p>
        <p className="font-heading text-2xl font-bold text-foreground">{fmt(event.amount)}</p>
      </div>

      <div className="glass-card p-3 rounded-xl text-xs text-muted-foreground space-y-1">
        <p>Liquid after closing: <span className="text-foreground font-semibold">{fmt(liquidAfter)}</span></p>
        <p>Emergency fund: <span className="text-foreground font-semibold">{fmt(emergencyFund)}</span></p>
      </div>

      <div className="grid gap-3">
        <button
          disabled={!!chosen || !canPayCash}
          onClick={() => onChoose('pay_cash')}
          className={`text-left p-4 rounded-xl border transition-all ${
            chosen === 'pay_cash' ? 'glass-card glow-border ring-2 ring-emerald-500/40'
            : 'glass-card hover:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-emerald-500" />
            <span className="font-heading font-semibold text-foreground">Pay from savings</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {canPayCash
              ? 'One-time hit, no follow-on cost. Cushion shrinks but the problem is gone.'
              : 'Not enough liquid cash on hand to cover this in full.'}
          </p>
        </button>
        <button
          disabled={!!chosen}
          onClick={() => onChoose('use_credit')}
          className={`text-left p-4 rounded-xl border transition-all ${
            chosen === 'use_credit' ? 'glass-card glow-border ring-2 ring-amber-500/40'
            : 'glass-card hover:border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-amber-500" />
            <span className="font-heading font-semibold text-foreground">Put on credit</span>
          </div>
          <p className="text-xs text-muted-foreground">Carry the balance and pay interest until it is gone. Repair cost grows over time.</p>
        </button>
        <button
          disabled={!!chosen}
          onClick={() => onChoose('postpone')}
          className={`text-left p-4 rounded-xl border transition-all ${
            chosen === 'postpone' ? 'glass-card glow-border ring-2 ring-destructive/40'
            : 'glass-card hover:border-destructive/50 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-destructive" />
            <span className="font-heading font-semibold text-foreground">Postpone the repair</span>
          </div>
          <p className="text-xs text-muted-foreground">Damage compounds. Eventual fix is larger, more disruptive, sometimes a safety issue.</p>
        </button>
      </div>

      <Button onClick={onContinue} disabled={!chosen} className="w-full">
        Continue to Mission Recap
      </Button>
    </div>
  );
}